import {
  ColumnSort,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table'
import Link from 'next/link'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { useLocalStorage } from 'react-use'

import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import get from 'lodash-es/get'
import sumBy from 'lodash-es/sumBy'
import useSWR from 'swr'

import { Product } from '@infura/packages.api'
import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { Loader } from '@infura/packages.ui/Loader'
import { Table } from '@infura/packages.ui/Table'
import { Text } from '@infura/packages.ui/Text'
import { ProjectRole, V1Project } from '@infura/packages.uks'

import { KeyServiceTableLoadingSkeleton } from 'components/dashboard/key/KeySkeletons'
import { ActionColumn } from 'components/dashboard/key/KeysList/ActionColumn'
import { TableRequestHealth } from 'components/dashboard/key/KeysList/TableRequestHealth'
import { NoKeyWarning } from 'components/dashboard/key/NoKeyWarning'
import { useSetSelectedApiKey } from 'components/keySelection/Context'

import useConstantStatsQuery from 'hooks/useStats'
import { useUKSProjects } from 'hooks/useUKSProjects'
import { useUKSServicePlan } from 'hooks/useUKSServicePlan'

import { AnalyticsQueryKeys } from 'lib/statsUtils'
import { getValueInThousands } from 'lib/usage/getTotalUsage'
import { getOnCreditPlan } from 'lib/utils/creditPlan'

const TEN_SECONDS = 10_000
type Row = {
  id: string
  name: string
  settingsLink: string
  updated: number
  creditUsageToday: string
  requestHealth: string
  requestsToday: string
}

const ColumnHeader = ({ text }: { text: string }) => (
  <Text className='text-base capitalize' theme='medium'>
    {text}
  </Text>
)

const CreditUsageToday = ({ id }: { id: string }) => {
  const res = useConstantStatsQuery(AnalyticsQueryKeys.CREDIT_USAGE_TODAY, Product.ETHEREUM, [id], undefined, {
    refreshInterval: TEN_SECONDS
  })
  const statsResult = getValueInThousands(sumBy(get(res.data, dataPath, initialValue), 'value') || 0)

  return (
    <>
      <Text className='w-[182px] truncate font-mono text-gray-700' data-testid='table-credit-usage'>
        {res.isLoading ? <Loader className={'h-5 w-16'} pulse /> : statsResult}
      </Text>
    </>
  )
}

const RequestsToday = ({ id }: { id: string }) => {
  const res = useConstantStatsQuery(AnalyticsQueryKeys.REQUESTS_UTC_TODAY, Product.ETHEREUM, [id], undefined, {
    refreshInterval: TEN_SECONDS
  })
  const statsResult = getValueInThousands(sumBy(get(res.data, dataPath, initialValue), 'value')) || 0

  return (
    <>
      <Text className='w-[182px] font-mono text-gray-700' data-testid='table-daily-requests'>
        {res.isLoading ? <Loader className={'h-5 w-16'} pulse /> : statsResult}
      </Text>
    </>
  )
}

const parseDate = (input: number | string): Dayjs => {
  if (typeof input === 'string') {
    return dayjs(input)
  }

  return dayjs(input * 1000)
}

const dataPath = ['data', 0, 'result']
const initialValue = [{ value: 0 }]

async function fetchProjectDataForTable(project: V1Project) {
  return {
    id: project.id,
    name: project.name,
    role: project.role,
    updated: project.updated
  }
}

const columnHelper = createColumnHelper<Row>()

const baseTrackingProps = {
  location: 'Dashboard API Key Table'
}

export default function KeysList({ role }: Readonly<{ role: ProjectRole | 'all-roles' }>) {
  const setSelectedKey = useSetSelectedApiKey()
  const { isValidating, projects: _projects } = useUKSProjects()
  const { loading: servicePlanIsLoading, servicePlan } = useUKSServicePlan()
  const isOnCreditPlan = getOnCreditPlan(servicePlan)

  let projects = Object.values(_projects || {})

  if (role !== 'all-roles') {
    projects = projects.filter(project => project.role === role)
  }

  const [sorting, setSorting] = useLocalStorage<Array<ColumnSort>>('infura-api-key-table-sort', [
    { desc: true, id: 'name' }
  ])

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: props => {
          const projectName = props.getValue()
          const id = props.row.original.id
          return (
            <Link
              className='text-l text-text-default mb-0 block w-[150px] cursor-pointer truncate'
              data-testid='table-key-name'
              href={'/key/all-endpoints'}
              onClick={() => {
                setSelectedKey(id)
                trackClickForSegmentAnalytics({
                  ...baseTrackingProps,
                  clickText: 'Key Name',
                  type: 'Text'
                })
              }}
              title={projectName}
            >
              {projectName}
            </Link>
          )
        },
        header: () => <ColumnHeader text='Name' />,
        id: 'name',
        meta: {
          header: {
            sortable: true,
            variant: 'button'
          }
        }
      }),
      columnHelper.accessor('requestHealth', {
        cell: ({ row }) => <TableRequestHealth id={row.original.id} />,
        header: () => <ColumnHeader text='Daily Request Health %' />,
        id: 'request-health'
      }),
      columnHelper.accessor('requestsToday', {
        cell: props => <RequestsToday id={props.row.original.id} />,
        header: () => <ColumnHeader text='Daily Requests' />,
        id: 'requests-today'
      }),
      ...(isOnCreditPlan
        ? [
            columnHelper.accessor('creditUsageToday', {
              cell: props => <CreditUsageToday id={props.row.original.id} />,
              header: () => <ColumnHeader text='Daily Credit Usage' />,
              id: 'credit-usage-today'
            })
          ]
        : []),
      columnHelper.accessor('updated', {
        cell: props => {
          const updated = parseDate(props.getValue())
          return (
            <>
              <Text className='w-[160px]' data-testid='table-key-updated'>
                {updated.format('MMM D, YYYY')}
              </Text>
            </>
          )
        },
        header: () => <ColumnHeader text='Updated' />,
        id: 'updated',
        meta: {
          header: {
            sortable: true,
            variant: 'button'
          }
        }
      }),
      columnHelper.accessor('id', {
        cell: props => {
          const id = props.row.original.id
          const name = props.row.original.name
          return <ActionColumn id={id} keyName={name} />
        },
        header: () => <ColumnHeader text='Actions' />,
        id: 'actions'
      })
    ],
    [isOnCreditPlan]
  )

  const { data: tableResults = [], isValidating: rowsValidating } = useSWR(
    ['KeysList', ...(projects || [])],
    async ([, ...p]) => {
      return Promise.all(p.map(project => fetchProjectDataForTable(project)))
    },
    { revalidateOnFocus: false }
  )

  if (isValidating || rowsValidating || servicePlanIsLoading) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <KeyServiceTableLoadingSkeleton />
      </div>
    )
  }

  return (
    <>
      <div
        className={clsx(
          'mb-6 w-full flex-col justify-center md:flex',
          projects.length === 0 ? 'bg-background-default rounded-md px-8 py-16 ' : 'pb-2'
        )}
      >
        {!isValidating && projects.length === 0 ? <NoKeyWarning /> : null}
        {tableResults?.length ? (
          <Table
            options={{
              columns,
              // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
              data: tableResults,
              getCoreRowModel: getCoreRowModel(),
              getPaginationRowModel: getPaginationRowModel(),
              getSortedRowModel: getSortedRowModel(),
              onSortingChange: setSorting as Dispatch<SetStateAction<Array<ColumnSort>>>,
              state: { sorting }
            }}
            pageSize={5}
            scroll
          />
        ) : null}
      </div>
    </>
  )
}
